import { Box, Flex, Grid, GridItem, Heading, Text, useMediaQuery } from '@chakra-ui/react';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { configFile } from '@/config';

interface Props {
  content?: boolean;
  startups?: boolean;
}

export default function AgileMethodsWithSEO({ content, startups }: Props) {
  const [isMobileScreen] = useMediaQuery('(max-width: 991px)');
  const { t } = useTranslation('services');

  return (
    <Flex
      direction="column"
      maxWidth="1216px"
      margin="0 auto"
      width={{ base: '90%', lg: '100%' }}
      mb="32px"
      gap="72px"
      pt={{ base: '40px', lg: '72px' }}
      pb={{ base: '40px', lg: '72px' }}
    >
      <Flex direction="column" alignItems="center" justifyContent="center" gap="16px">
        <Heading
          as="h2"
          width="90%"
          fontSize={{ base: '36px', lg: '48px' }}
          lineHeight={{ base: '40px', lg: '48px' }}
          fontWeight="800"
          textAlign="center"
          color="blue.900"
          ml={{ base: '0', lg: '0' }}
        >
          {content && t('content.agileMethodsWithSEO.title')}
          {startups && t('SEOForStartups.agileMethodsWithSEO.title')}
        </Heading>
        <Text fontSize="16px" lineHeight="24px" color="gray.600" textAlign="center" w={{ base: 'auto', lg: '798px' }}>
          {content && t('content.agileMethodsWithSEO.description')}
          {startups && t('SEOForStartups.agileMethodsWithSEO.description')}
        </Text>
      </Flex>
      <Grid
        w={{ base: '100%', lg: '100%' }}
        templateColumns={{
          base: '100%',
          lg: '355px 1fr 188px 188px 1fr 355px',
        }}
        templateRows={{
          base: '170px 45px 1fr 45px 1fr 45px 1fr 45px 1fr',
          lg: '94px 94px 94px 94px 94px 94px',
        }}
      >
        <GridItem colStart={1} rowStart={{ base: 3, lg: 1 }} rowEnd={{ base: 3, lg: 3 }}>
          <Flex
            direction="column"
            p="24px"
            bgColor="#F1F6FF"
            borderRadius="16px"
            w="100%"
            gap="8px"
            justifySelf="center"
          >
            <Text fontSize="24px" lineHeight="32px" fontWeight="700" color="blue.600">
              {content && t('content.agileMethodsWithSEO.items.title1')}
              {startups && t('SEOForStartups.agileMethodsWithSEO.items.title1')}
            </Text>
            <Text fontSize="14px" lineHeight="20px" color="gray.600">
              {content && t('content.agileMethodsWithSEO.items.description1')}
              {startups && t('SEOForStartups.agileMethodsWithSEO.items.description1')}
            </Text>
          </Flex>
        </GridItem>
        <GridItem colStart={{ base: 1, lg: 6 }} rowStart={{ base: 5, lg: 1 }} rowEnd={{ base: 5, lg: 3 }}>
          <Flex
            direction="column"
            p="24px"
            bgColor="#F1F6FF"
            borderRadius="16px"
            w="100%"
            gap="8px"
            justifySelf="center"
          >
            <Text fontSize="24px" lineHeight="32px" fontWeight="700" color="blue.600">
              {content && t('content.agileMethodsWithSEO.items.title2')}
              {startups && t('SEOForStartups.agileMethodsWithSEO.items.title2')}
            </Text>
            <Text fontSize="14px" lineHeight="20px" color="gray.600">
              {content && t('content.agileMethodsWithSEO.items.description2')}
              {startups && t('SEOForStartups.agileMethodsWithSEO.items.description2')}
            </Text>
          </Flex>
        </GridItem>
        <GridItem colStart={1} rowStart={{ base: 7, lg: 5 }} rowEnd={7}>
          <Flex
            direction="column"
            p="24px"
            bgColor="#F1F6FF"
            borderRadius="16px"
            w="100%"
            gap="8px"
            justifySelf="center"
          >
            <Text fontSize="24px" lineHeight="32px" fontWeight="700" color="blue.600">
              {content && t('content.agileMethodsWithSEO.items.title3')}
              {startups && t('SEOForStartups.agileMethodsWithSEO.items.title3')}
            </Text>
            <Text fontSize="14px" lineHeight="20px" color="gray.600">
              {content && t('content.agileMethodsWithSEO.items.description3')}
              {startups && t('SEOForStartups.agileMethodsWithSEO.items.description3')}
            </Text>
          </Flex>
        </GridItem>
        <GridItem colStart={{ base: 1, lg: 6 }} rowStart={{ base: 9, lg: 5 }} rowEnd={{ base: 9, lg: 7 }}>
          <Flex
            direction="column"
            p="24px"
            bgColor="#F1F6FF"
            borderRadius="16px"
            w="100%"
            gap="8px"
            justifySelf="center"
          >
            <Text fontSize="24px" lineHeight="32px" fontWeight="700" color="blue.600">
              {content && t('content.agileMethodsWithSEO.items.title4')}
              {startups && t('SEOForStartups.agileMethodsWithSEO.items.title4')}
            </Text>
            <Text fontSize="14px" lineHeight="20px" color="gray.600">
              {content && t('content.agileMethodsWithSEO.items.description4')}
              {startups && t('SEOForStartups.agileMethodsWithSEO.items.description4')}
            </Text>
          </Flex>
        </GridItem>

        <GridItem
          colStart={{ base: 1, lg: 3 }}
          colEnd={{ base: 1, lg: 5 }}
          rowStart={{ base: 1, lg: 3 }}
          rowEnd={{ base: 1, lg: 5 }}
          position="relative"
        >
          <Box
            bgColor={configFile.tertiaryAccentColor}
            boxShadow="0px 20.08816146850586px 36.82829666137695px -8.370067596435547px rgba(0, 0, 0, 0.16)"
            borderRadius="9999px"
            border="1px solid #EDF2F7"
            h={{ base: '170px', lg: '230px' }}
            w={{ base: '170px', lg: '230px' }}
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            position="absolute"
          />
          <Box
            bgColor={configFile.tertiaryAccentColor}
            boxShadow="0px 20.08816146850586px 36.82829666137695px -8.370067596435547px rgba(0, 0, 0, 0.16)"
            borderRadius="9999px"
            border="1px solid #EDF2F7"
            h={{ base: '163px', lg: '223px' }}
            w={{ base: '163px', lg: '223px' }}
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            position="absolute"
          />
          {isMobileScreen && (
            <Image
              src="/images/default/icon-logo.svg"
              width="55"
              height="54"
              alt="Ecto"
              style={{
                width: '55px',
                position: 'relative',
                zIndex: 3,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
          {!isMobileScreen && (
            <Image
              src="/images/default/icon-logo.svg"
              width="78"
              height="77"
              alt="Ecto"
              style={{
                position: 'relative',
                zIndex: 3,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
        </GridItem>

        <GridItem colStart={3} colEnd={5} rowStart={2} rowEnd={6} position="relative">
          <Box
            display={{ base: 'none', lg: 'flex' }}
            position="absolute"
            top="0"
            left="0"
            height="100%"
            width="100%"
            zIndex={-1}
            _before={{
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              height: '2px',
              width: '141%',
              background: 'teal.400',
              transform: 'translate(-50%, -50%) rotate(45deg)',
            }}
            _after={{
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              height: '2px',
              width: '141%',
              background: 'teal.400',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
            }}
          />
        </GridItem>
        <GridItem colStart={2} colEnd={3} rowStart={2} display={{ base: 'none', lg: 'flex' }}>
          <Box w="100%" h="100%" borderTop="2px solid #38B2AC" />
        </GridItem>
        <GridItem colStart={5} colEnd={6} rowStart={2} display={{ base: 'none', lg: 'flex' }}>
          <Box w="100%" h="100%" borderTop="2px solid #38B2AC" />
        </GridItem>
        <GridItem colStart={2} colEnd={3} rowStart={6} display={{ base: 'none', lg: 'flex' }}>
          <Box w="100%" h="100%" borderTop="2px solid #38B2AC" />
        </GridItem>
        <GridItem colStart={5} colEnd={6} rowStart={6} display={{ base: 'none', lg: 'flex' }}>
          <Box w="100%" h="100%" borderTop="2px solid #38B2AC" />
        </GridItem>

        {isMobileScreen && (
          <>
            <GridItem colStart={1} rowStart={2} display={{ base: 'flex', lg: 'none' }} position="relative">
              <Box position="absolute" top="0" bottom="0" left="50%" borderLeft="2px solid #38b2ac" />
            </GridItem>
            <GridItem colStart={1} rowStart={4} display={{ base: 'flex', lg: 'none' }} position="relative">
              <Box position="absolute" top="0" bottom="0" left="50%" borderLeft="2px solid #38b2ac" />
            </GridItem>
            <GridItem colStart={1} rowStart={6} display={{ base: 'flex', lg: 'none' }} position="relative">
              <Box position="absolute" top="0" bottom="0" left="50%" borderLeft="2px solid #38b2ac" />
            </GridItem>
            <GridItem colStart={1} rowStart={8} display={{ base: 'flex', lg: 'none' }} position="relative">
              <Box position="absolute" top="0" bottom="0" left="50%" borderLeft="2px solid #38b2ac" />
            </GridItem>
          </>
        )}
      </Grid>
    </Flex>
  );
}
